import React from 'react'

import {
  UserMetadata,
  UserNotification,
  FeatureFlag,
} from '../../../common/validations'
import { ResidentNotificationCategory } from '../../../common/types'
import { useAPIGetUserMetadataSuspense } from '../../api/apiHooks/useAPIGetUserMetadata'
import { useAPIGetFeatureFlagsSuspense } from '../../api/apiHooks/useAPIGetFeatureFlags'

import { useCurrentContract } from 'hooks/useCurrentContract/useCurrentContract'
import { useAPIGetUserNotificationsSuspense } from 'api/apiHooks/useAPIGetUserNotifications'

interface DataLoaderProps {
  children: (data: Data) => React.ReactNode
  notificationCategories?: ResidentNotificationCategory[]
}

interface Data {
  notifications: UserNotification[]
  userMetadata: UserMetadata
  featureFlags: FeatureFlag
}

export const DataLoader: React.FC<DataLoaderProps> = ({
  children,
  notificationCategories,
}) => {
  const { currentContractId } = useCurrentContract()
  const { data: notifications } = useAPIGetUserNotificationsSuspense()
  const { data: userMetadata } = useAPIGetUserMetadataSuspense()
  const { data: featureFlags } = useAPIGetFeatureFlagsSuspense()

  const filteredNotifications = notifications
    // Filter by contract lifecycle category:
    .filter((notification) => {
      if (!notificationCategories) {
        return true
      }

      return notificationCategories.includes(notification.category)
    })
    // Filter by current contract:
    .filter((notification) =>
      notification.contractUuid.includes(currentContractId)
    )
    // Filter out closed notifications:
    .filter((notification) => !notification.closedAt)
    // Filter out unsupported notifications
    .filter((notification) =>
      [
        ResidentNotificationCategory.arrear,
        ResidentNotificationCategory.contract_termination_request_approval_needed,
        ResidentNotificationCategory.contract_termination_request_kenno_rejected,
        ResidentNotificationCategory.contract_termination_request_tenant_rejected,
        ResidentNotificationCategory.contract_modification_request_approval_needed,
        ResidentNotificationCategory.contract_modification_request_kenno_rejected,
        ResidentNotificationCategory.contract_modification_request_tenant_rejected,
        ResidentNotificationCategory.message,
      ].includes(notification.category)
    )

  return children({
    notifications: filteredNotifications,
    userMetadata: userMetadata!,
    featureFlags,
  })
}
